import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedSwitch } from './ThemedSwitch';
import { getFirestoreDb } from '../../utils/calendarShareService';
import {
  DEFAULT_RIDER_REMINDER,
  SHARE_SCHEDULE_MODULE_OPTIONS,
  buildShareScheduleIndicators,
  formatScheduleDateLabel,
  formatGlobalTourScheduleSummary,
  listDayScheduleExceptions,
  saveRiderReminderSettings,
  subscribeTourShareSettings,
} from '../../utils/tourShareScheduleService';
import { DayScheduleStatusMessage } from './DayScheduleSettingsPanel';
import { TourPartyPermissionsPanel } from './TourPartyPermissionsPanel';

function ScheduleOverviewRow({ dateKey, items, theme, styles }) {
  const sendItems = items.filter((i) => i.type === 'send');
  const showItems = items.filter((i) => i.type === 'show');
  return (
    <View style={styles.overviewRow}>
      <Text style={styles.overviewDate}>{formatScheduleDateLabel(dateKey)}</Text>
      <View style={{ flex: 1, gap: 4 }}>
        {sendItems.map((item) => (
          <Text key={`s-${item.scheduleId}`} style={styles.overviewSend} numberOfLines={2}>
            <Ionicons name="notifications-outline" size={12} color={theme.status?.warning || '#f59e0b'} />
            {'  '}
            Sends {(item.modules || []).join(', ')}
            {item.showDateKey ? ` · for ${formatScheduleDateLabel(item.showDateKey)}` : ''}
          </Text>
        ))}
        {showItems.map((item) => (
          <Text key={`sh-${item.scheduleId}`} style={styles.overviewShow} numberOfLines={2}>
            <Ionicons name="paper-plane-outline" size={12} color={theme.brand.accent} />
            {'  '}
            Share {(item.modules || []).join(', ')}
            {item.sendDateKey ? ` · reminder ${formatScheduleDateLabel(item.sendDateKey)}` : ''}
          </Text>
        ))}
      </View>
    </View>
  );
}

export function TourSettingsManager({
  visible,
  onClose,
  ownerId,
  tourId,
  tourName,
  showDateKeys = [],
  onToast,
  variant = 'modal',
  currentUserId = null,
  acceptedMembers = [],
  tourShareSettings = null,
}) {
  const { theme, isDarkMode } = useTheme();
  const db = getFirestoreDb();
  const [settings, setSettings] = useState(() => ({
    riderReminder: { ...DEFAULT_RIDER_REMINDER },
    dayScheduleOverrides: {},
  }));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draftReminder, setDraftReminder] = useState({ ...DEFAULT_RIDER_REMINDER });

  useEffect(() => {
    if (!visible || !ownerId || !tourId) return undefined;
    setLoading(true);
    return subscribeTourShareSettings(db, ownerId, tourId, (s) => {
      setSettings(s);
      setDraftReminder({
        ...DEFAULT_RIDER_REMINDER,
        ...(s.riderReminder || {}),
        modules: s.riderReminder?.modules?.length
          ? s.riderReminder.modules
          : DEFAULT_RIDER_REMINDER.modules,
      });
      setLoading(false);
    });
  }, [visible, ownerId, tourId, db]);

  const indicatorsByDate = useMemo(
    () => buildShareScheduleIndicators({ settings, showDateKeys }),
    [settings, showDateKeys]
  );

  const overviewDates = useMemo(
    () => Object.keys(indicatorsByDate).sort(),
    [indicatorsByDate]
  );

  const dayExceptions = useMemo(
    () => listDayScheduleExceptions(settings, showDateKeys),
    [settings, showDateKeys]
  );

  const toggleDraftModule = (mod) => {
    setDraftReminder((prev) => {
      const has = (prev.modules || []).includes(mod);
      const next = has
        ? (prev.modules || []).filter((m) => m !== mod)
        : [...(prev.modules || DEFAULT_RIDER_REMINDER.modules), mod];
      return { ...prev, modules: next.length ? next : [mod] };
    });
  };

  const saveReminder = useCallback(async () => {
    try {
      setSaving(true);
      await saveRiderReminderSettings(db, ownerId, tourId, draftReminder);
      onToast?.('Tour settings saved.', 'success');
    } catch (err) {
      onToast?.(err?.message || 'Could not save settings.', 'error');
    } finally {
      setSaving(false);
    }
  }, [db, ownerId, tourId, draftReminder, onToast]);

  const styles = useMemo(() => createStyles(theme, isDarkMode), [theme, isDarkMode]);

  if (!visible) return null;

  const body = loading ? (
    <ActivityIndicator color={theme.brand.accent} style={{ marginTop: 24 }} />
  ) : (
    <>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Permissions</Text>
        <Text style={styles.sectionHint}>
          Primarily set here. Each accepted tour party member can be hidden, view-only, or edit per calendar section.
        </Text>
        <TourPartyPermissionsPanel
          ownerId={ownerId}
          currentUserId={currentUserId || ownerId}
          tourId={tourId}
          tourShareSettings={tourShareSettings}
          acceptedMembers={acceptedMembers}
          theme={theme}
          onError={(text) => onToast?.(text, 'error')}
          onSuccess={(text) => onToast?.(text, 'success')}
          defaultOpen
        />
      </View>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Global scheduled send</Text>
        <Text style={styles.sectionHint}>
          Default for every show day on this tour. To change one day only, use Schedule for this day under that date in the calendar sidebar.
        </Text>
        <View style={styles.globalSummaryBox}>
          <Text style={styles.globalSummaryText}>
            {formatGlobalTourScheduleSummary({ riderReminder: draftReminder })}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.rowLabel}>Enable scheduled sends</Text>
          <ThemedSwitch
            value={draftReminder.enabled}
            onValueChange={(v) => setDraftReminder((p) => ({ ...p, enabled: v }))}
          />
        </View>
        {draftReminder.enabled ? (
          <>
            <View style={styles.row}>
              <Text style={styles.rowLabel}>Notify days before show</Text>
              <TextInput
                style={styles.numInput}
                value={String(draftReminder.daysBefore ?? 1)}
                onChangeText={(t) =>
                  setDraftReminder((p) => ({
                    ...p,
                    daysBefore: Math.max(0, Number(t.replace(/\D/g, '')) || 0),
                  }))
                }
                keyboardType="number-pad"
                placeholderTextColor={theme.input.placeholder}
              />
            </View>
            <Text style={styles.fieldLabel}>Default sections</Text>
            <View style={styles.moduleGrid}>
              {SHARE_SCHEDULE_MODULE_OPTIONS.map((mod) => {
                const on = (draftReminder.modules || []).includes(mod);
                return (
                  <TouchableOpacity
                    key={mod}
                    style={[styles.moduleChip, on && styles.moduleChipActive]}
                    onPress={() => toggleDraftModule(mod)}
                  >
                    <Text style={[styles.moduleChipText, on && styles.moduleChipTextActive]}>{mod}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <View style={styles.row}>
              <Text style={styles.rowLabel}>In-app notification</Text>
              <ThemedSwitch
                value={draftReminder.inApp !== false}
                onValueChange={(v) => setDraftReminder((p) => ({ ...p, inApp: v }))}
              />
            </View>
            <View style={styles.row}>
              <Text style={styles.rowLabel}>Email notification</Text>
              <ThemedSwitch
                value={draftReminder.email !== false}
                onValueChange={(v) => setDraftReminder((p) => ({ ...p, email: v }))}
              />
            </View>
          </>
        ) : null}
        <TouchableOpacity
          style={[styles.primaryBtn, saving && styles.primaryBtnDisabled]}
          onPress={saveReminder}
          disabled={saving}
        >
          <Text style={styles.primaryBtnText}>Save tour settings</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Day exceptions</Text>
        <Text style={styles.sectionHint}>
          Read-only summary. Edit exceptions under each show date in the sidebar — expand Schedule for this day.
        </Text>
        {dayExceptions.length === 0 ? (
          <Text style={styles.emptyHint}>No day-specific exceptions yet.</Text>
        ) : (
          dayExceptions.map(({ dateKey }) => (
            <View key={dateKey} style={styles.exceptionRow}>
              <Text style={styles.exceptionDate}>{formatScheduleDateLabel(dateKey)}</Text>
              <DayScheduleStatusMessage
                settings={settings}
                dateKey={dateKey}
                isShowDay
                style={styles.exceptionDetail}
                theme={theme}
              />
            </View>
          ))
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Calendar overview</Text>
        <Text style={styles.sectionHint}>
          Amber = notification send day · Teal = show day with scheduled share
        </Text>
        {overviewDates.length === 0 ? (
          <Text style={styles.emptyHint}>No scheduled activity yet.</Text>
        ) : (
          overviewDates.map((dk) => (
            <ScheduleOverviewRow
              key={dk}
              dateKey={dk}
              items={indicatorsByDate[dk]}
              theme={theme}
              styles={styles}
            />
          ))
        )}
      </View>

      <View style={styles.legend}>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: theme.status?.warning || '#f59e0b' }]} />
          <Text style={styles.legendText}>Send / reminder day</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: theme.brand.accent }]} />
          <Text style={styles.legendText}>Show day share</Text>
        </View>
      </View>
    </>
  );

  if (variant === 'sidebar') {
    return <View style={styles.sidebarBody}>{body}</View>;
  }

  return (
    <View style={styles.overlay}>
      <View style={styles.panel}>
        <View style={styles.panelHeader}>
          <View style={{ flex: 1 }}>
            <Text style={styles.panelTitle}>Tour settings</Text>
            <Text style={styles.panelSub}>
              {tourName || 'Tour'} · global defaults for every show day
            </Text>
          </View>
          <TouchableOpacity onPress={onClose} hitSlop={12} style={styles.closeBtn}>
            <Ionicons name="close" size={22} color={isDarkMode ? '#f2f2f2' : theme.text.primary} />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          {body}
        </ScrollView>
      </View>
    </View>
  );
}

function createStyles(theme, isDarkMode) {
  const textPrimary = isDarkMode ? '#f2f2f2' : theme.text.primary;
  const textSecondary = isDarkMode ? '#c8c8c8' : theme.text.secondary;
  const textTertiary = isDarkMode ? '#a0a0a0' : theme.text.tertiary;
  const panelBg = isDarkMode ? 'rgba(18,18,22,0.98)' : 'rgba(255,255,255,0.98)';
  return StyleSheet.create({
    sidebarBody: { gap: 20, paddingBottom: 8 },
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.55)',
      zIndex: 1200,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 16,
    },
    panel: {
      width: '100%',
      maxWidth: 560,
      maxHeight: '92%',
      backgroundColor: panelBg,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme.ui.border,
      overflow: 'hidden',
    },
    panelHeader: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      padding: 16,
      borderBottomWidth: 1,
      borderBottomColor: theme.ui.border,
    },
    panelTitle: { fontSize: 18, fontWeight: '800', color: textPrimary },
    panelSub: { fontSize: 12, color: textSecondary, marginTop: 4 },
    closeBtn: { padding: 4 },
    scroll: { flex: 1 },
    scrollContent: { padding: 16, paddingBottom: 32, gap: 20 },
    section: { gap: 10 },
    sectionTitle: { fontSize: 14, fontWeight: '800', color: textPrimary, letterSpacing: 0.5 },
    sectionHint: { fontSize: 12, color: textSecondary, lineHeight: 17 },
    globalSummaryBox: {
      padding: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
    },
    globalSummaryText: { fontSize: 12, lineHeight: 17, color: textSecondary },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      paddingVertical: 6,
    },
    rowLabel: { fontSize: 13, color: textPrimary, flex: 1 },
    fieldLabel: {
      fontSize: 10,
      fontWeight: '800',
      color: textTertiary,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    numInput: {
      width: 56,
      textAlign: 'center',
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 8,
      paddingVertical: 6,
      color: theme.input.text,
      backgroundColor: theme.input.background,
    },
    moduleGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    moduleChip: {
      paddingHorizontal: 8,
      paddingVertical: 5,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    moduleChipActive: { borderColor: theme.brand.accent, backgroundColor: `${theme.brand.accent}18` },
    moduleChipText: { fontSize: 10, fontWeight: '700', color: textSecondary },
    moduleChipTextActive: { color: theme.brand.accent },
    primaryBtn: {
      marginTop: 8,
      backgroundColor: theme.brand.accent,
      borderRadius: 10,
      paddingVertical: 10,
      alignItems: 'center',
    },
    primaryBtnDisabled: { opacity: 0.5 },
    primaryBtnText: { color: '#fff', fontWeight: '800', fontSize: 13 },
    emptyHint: { fontSize: 12, color: textTertiary, fontStyle: 'italic' },
    exceptionRow: {
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: theme.ui.border,
      gap: 4,
    },
    exceptionDate: { fontSize: 12, fontWeight: '800', color: textPrimary },
    exceptionDetail: { fontSize: 11, color: textSecondary, lineHeight: 16 },
    overviewRow: {
      flexDirection: 'row',
      gap: 10,
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: theme.ui.border,
    },
    overviewDate: { width: 88, fontSize: 11, fontWeight: '800', color: textPrimary },
    overviewSend: { fontSize: 11, color: theme.status?.warning || '#f59e0b', lineHeight: 16 },
    overviewShow: { fontSize: 11, color: theme.brand.accent, lineHeight: 16 },
    legend: { flexDirection: 'row', gap: 16, paddingTop: 4 },
    legendItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    legendDot: { width: 8, height: 8, borderRadius: 4 },
    legendText: { fontSize: 11, color: textSecondary },
  });
}

/** Share + Tour settings icon actions for sidebar section headers. */
export function SidebarShareSettingsActions({ theme, styles, onShare, onSettings, onPrint }) {
  return (
    <View style={styles.selectedDayEditorActions}>
      {onPrint ? (
        <TouchableOpacity
          style={styles.selectedDayIconActionBtn}
          onPress={onPrint}
          accessibilityLabel="Print or save as PDF"
        >
          <Ionicons name="print-outline" size={16} color={theme.brand.primary} />
        </TouchableOpacity>
      ) : null}
      <TouchableOpacity
        style={styles.selectedDayIconActionBtn}
        onPress={onShare}
        accessibilityLabel="Share this day"
      >
        <Ionicons name="paper-plane-outline" size={16} color={theme.brand.accent} />
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.selectedDayIconActionBtn}
        onPress={onSettings}
        accessibilityLabel="Tour settings"
      >
        <Ionicons name="settings-outline" size={16} color={theme.brand.primary} />
      </TouchableOpacity>
    </View>
  );
}
